import React, { useEffect, useState } from "react";
import MorningMeetingDetailsContainer from "./MorningMeetingDetails.styles";
import HomeList from "components/Screens/Home/HomeList/HomeList";
import { useSettings } from "context/Settings.context";
import * as Database from "utils/database.utils";
import {
    AgendaItem,
    MorningMeetingDetailsDto,
    ZoomFetchFileType,
} from "./MorningMeetingDetails.types";
import {
    faCalendar,
    faCheck,
    faDownload,
    faEye,
    faFileText,
    faMultiply,
    faPencil,
    faPlus,
    faReorder,
    faSave,
    faVideo,
} from "@fortawesome/free-solid-svg-icons";
import Button from "../../../components/Common/Button/Button";
import AgendaItemRow from "components/Screens/Company/AgendaItemRow/AgendaItemRow";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { toDateAndTimeSeconds } from "../../../utils/general.utils";
import Tooltip from "@mui/material/Tooltip";
import Alert from "@mui/material/Alert";
import { useHistory } from "react-router-dom";
import MorningMeetingModal from "../MorningMeetingModal/MorningMeetingModal";
import { MorningMeetingForm } from "../MorningMeetingModal/MorningMeetingModal.types";
import FetchZoomFileModal from "./FetchZoomFileModal";
import FetchRecordingUrlModal from "./FetchRecordingUrlModal";
import DownloadMeetingFilesModal from "./DownloadMeetingFilesModal";
import { Link } from "@mui/material";
import { NotesType } from "../GeneratedNotesDashboard/GeneratedNotesDashboard.types";

const NOTES_GENERATION_POLL_INTERVAL = 10000; // 10 seconds
const NOTES_GENERATION_POLL_TIMEOUT = 900000; // 15 minutes

const MorningMeetingDetails = () => {
    const history = useHistory();
    const { setShowLoadingToast, setToast } = useSettings();
    const [morningMeetingDetails, setMorningMeetingDetails] =
        useState<MorningMeetingDetailsDto | null>(null);
    const [loading, setLoading] = useState(false);
    const [meetingId, setMeetingId] = useState("");
    const [canCancelMeeting, setCanCancelMeeting] = useState(false);
    const [cancelMeetingReason, setCancelMeetingReason] = useState("");
    const [isReordering, setIsReordering] = useState(false);
    const [reorderedItems, setReorderedItems] = useState<AgendaItem[]>([]);
    const [modalOpen, setModalOpen] = useState(false);
    const [fetchZoomFileModalType, setFetchZoomFileModalType] =
        useState<ZoomFetchFileType | null>(null);
    const [recordingUrlModalOpen, setRecordingUrlModalOpen] = useState(false);
    const [downloadFilesModalOpen, setDownloadFilesModalOpen] = useState(false);
    const [notesGenerationPollInterval, setNotesGenerationPollInterval] =
        useState<NodeJS.Timeout | null>(null);

    const availableMeetingFiles =
        morningMeetingDetails?.files?.filter(
            (f) => !f.deleted && !f.archived
        ) ?? [];

    const getMorningMeetingDetails = async (morning_meeting_id: string) => {
        setLoading(true);

        const response = await Database.get(
            `api/bqmq_voting/morning-meetings/${morning_meeting_id}`
        );
        if (response.ok) {
            const data = await response.json();
            setMorningMeetingDetails(data.data);
            setReorderedItems(data.data.agenda_items);
        }

        setLoading(false);
    };

    const cancelMeeting = async () => {
        setLoading(true);

        const response = await Database.post(
            `api/bqmq_voting/morning-meetings/${morningMeetingDetails?.id}/cancel`,
            {}
        );

        setLoading(false);

        if (response.ok) {
            getMorningMeetingDetails(meetingId);
        } else {
            setToast({
                show: true,
                type: "error",
                message: "Failed to approve meeting cancelation",
            });
        }
    };

    const saveNewOrder = async () => {
        setLoading(true);

        const payload = reorderedItems.map((item) => ({
            id: item.id,
            order: item.order,
        }));

        const response = await Database.patch(
            `api/bqmq_voting/morning-meetings/${morningMeetingDetails?.id}/agenda-items/reorder`,
            payload
        );
        setLoading(false);

        if (response.ok) {
            getMorningMeetingDetails(meetingId);
        }
    };

    const shouldShowFetchTranscriptButton = (): boolean => {
        if (!morningMeetingDetails || morningMeetingDetails.cancelled)
            return false;

        const hasTranscript = morningMeetingDetails.files?.some(
            (file) =>
                file.type === "TRANSCRIPT" && !file.deleted && !file.archived
        );

        if (hasTranscript) return false;

        // Meeting usually takes up to an hour, then we give another hour for transcript generation
        // and if by then the transcript is not available, we show the button to fetch it
        const meetingDate = new Date(morningMeetingDetails.date);
        const twoHoursAfterMeeting = new Date(meetingDate);
        twoHoursAfterMeeting.setHours(twoHoursAfterMeeting.getHours() + 2);

        const now = new Date();

        return now >= twoHoursAfterMeeting;
    };

    const shouldShowWatchRecordingButton = (): boolean => {
        if (!morningMeetingDetails || morningMeetingDetails.cancelled) {
            return false;
        }
        const meetingStart = new Date(morningMeetingDetails.date);
        return new Date() >= meetingStart;
    };

    const shouldShowGenerateNotesButton = (): boolean => {
        if (!morningMeetingDetails) return false;

        const hasTranscript = morningMeetingDetails.files?.some(
            (file) =>
                file.type === "TRANSCRIPT" && !file.deleted && !file.archived
        );

        if (!hasTranscript) return false;

        const hasMeetingNotes = morningMeetingDetails.files?.some(
            (file) =>
                file.type === "MEETING_NOTES" && !file.deleted && !file.archived
        );

        return !hasMeetingNotes;
    };

    const shouldShowReviewGeneratedNotesButton = (): boolean => {
        if (!morningMeetingDetails) return false;

        const hasMeetingNotesFile = morningMeetingDetails.files?.some(
            (file) =>
                file.type === "MEETING_NOTES" && !file.deleted && !file.archived
        );

        // Allow user to navigate to note review page even when there are no unconfirmed notes
        // but there's a notes file, because this could mean that user deleted
        // all the unapproved notes manually and need a way to regenerate them.
        // Ideally there shouldn't be a mix of approved/unapproved notes, so the
        // contains_confirmed_notes check is there to assure only view or review button is shown
        return (
            morningMeetingDetails.contains_unconfirmed_notes ||
            (hasMeetingNotesFile &&
                !morningMeetingDetails?.contains_confirmed_notes)
        );
    };

    const handleFetchTranscript = async (zoomMeetingId: string) => {
        setLoading(true);
        setFetchZoomFileModalType(null);

        // Format date as YYYY-MM-DD
        const meetingDate = morningMeetingDetails?.date
            ? new Date(morningMeetingDetails.date).toISOString().split("T")[0]
            : null;

        const response = await Database.post(
            `api/bqmq_voting/morning-meetings/${meetingId}/transcript/fetch`,
            {
                zoom_meeting_id: zoomMeetingId,
                meeting_date: meetingDate,
            }
        );

        setLoading(false);

        if (response.ok) {
            setToast({
                show: true,
                type: "success",
                message: "Transcript fetched successfully",
            });
            await getMorningMeetingDetails(meetingId);
        } else {
            const errorData = await response.json().catch(() => ({}));
            setToast({
                show: true,
                type: "error",
                message: errorData.message || "Failed to fetch transcript",
            });
        }
    };

    const handleWatchRecordingClick = () => {
        const url = morningMeetingDetails?.recording_url;
        if (url) {
            window.open(url, "_blank", "noopener,noreferrer");
        } else {
            setRecordingUrlModalOpen(true);
        }
    };

    const handleFetchRecordingUrlConfirm = async (zoomMeetingId: string) => {
        setLoading(true);
        setRecordingUrlModalOpen(false);

        const meetingDate = morningMeetingDetails?.date
            ? new Date(morningMeetingDetails.date).toISOString().split("T")[0]
            : null;

        const response = await Database.post(
            `api/bqmq_voting/morning-meetings/${meetingId}/recording/fetch`,
            {
                zoom_meeting_id: zoomMeetingId,
                meeting_date: meetingDate,
            }
        );

        setLoading(false);

        if (response.ok) {
            const data = await response.json().catch(() => ({}));
            const url = data.recording_url as string | undefined;
            if (url) {
                window.open(url, "_blank", "noopener,noreferrer");
            }
            setToast({
                show: true,
                type: "success",
                message: data.message || "Recording link saved",
            });
            await getMorningMeetingDetails(meetingId);
        } else {
            const errorData = await response.json().catch(() => ({}));
            setToast({
                show: true,
                type: "error",
                message: errorData.message || "Failed to fetch recording link",
            });
        }
    };

    const handleFetchZoomFileConfirm = (zoomMeetingId: string) => {
        if (fetchZoomFileModalType === "TRANSCRIPT") {
            handleFetchTranscript(zoomMeetingId);
        }
        setFetchZoomFileModalType(null);
    };

    const reorderItems = (newItems: AgendaItem[]) => {
        newItems.forEach((item, index) => {
            item.order = index + 1;
        });
        setReorderedItems(newItems);
    };

    const openNewAgendaItemPage = () => {
        history.push(`/portfolio/morning-meetings/${meetingId}/agenda-item`);
    };

    const openGeneratedNotesPage = (notesType: NotesType) => {
        history.push(
            `/portfolio/morning-meetings/${meetingId}/generated-notes/${notesType}`
        );
    };

    const updateMorningMeeting = async (formValue: MorningMeetingForm) => {
        setShowLoadingToast({
            show: true,
            message: "UPDATING",
        });

        const payload = {
            id: meetingId,
            title: formValue.title,
            date: formValue.date?.toISOString(),
        };

        const response = await Database.patch(
            "api/bqmq_voting/morning-meetings",
            payload
        );

        if (response.ok) {
            setModalOpen(false);
            setShowLoadingToast({
                show: false,
                message: "UPDATING",
            });
            await getMorningMeetingDetails(meetingId);
        }
    };

    const generateNotes = async () => {
        const response = await Database.post(
            `api/bqmq_voting/morning-meetings/${morningMeetingDetails?.id}/generate`,
            {}
        );

        if (response.ok) {
            setToast({
                show: true,
                type: "success",
                message: "Notes are being generated",
            });

            // Refresh meeting details to get updated is_generating_notes flag
            await getMorningMeetingDetails(meetingId);

            // Start polling for notes completion
            const pollInterval = setInterval(async () => {
                await getMorningMeetingDetails(meetingId);
            }, NOTES_GENERATION_POLL_INTERVAL);

            setNotesGenerationPollInterval(pollInterval);

            // Stop polling after 15 minutes max
            setTimeout(() => {
                if (pollInterval) {
                    clearInterval(pollInterval);
                    setNotesGenerationPollInterval(null);
                }
            }, NOTES_GENERATION_POLL_TIMEOUT);
        }
    };

    useEffect(() => {
        const morningMeetingId = window.location.pathname.split("/").pop()!!;
        setMeetingId(morningMeetingId);

        getMorningMeetingDetails(morningMeetingId);
    }, []);

    useEffect(() => {
        if (loading) {
            setShowLoadingToast({
                show: true,
                message: "LOADING",
            });
        } else {
            setShowLoadingToast({
                show: false,
                message: "LOADING",
            });
        }
    }, [loading]);

    useEffect(() => {
        if (morningMeetingDetails) {
            if (morningMeetingDetails.cancelled) {
                return;
            }

            const isEmptyAgenda =
                morningMeetingDetails.agenda_items.length === 0;
            setCanCancelMeeting(isEmptyAgenda);

            if (!isEmptyAgenda) {
                setCancelMeetingReason(
                    "Meeting cannot be cancelled because it has agenda items"
                );
            }

            const hasMeetingNotes = morningMeetingDetails.files?.some(
                (file) =>
                    file.type === "MEETING_NOTES" &&
                    !file.deleted &&
                    !file.archived
            );

            if (notesGenerationPollInterval && hasMeetingNotes) {
                clearInterval(notesGenerationPollInterval);
                setNotesGenerationPollInterval(null);
                setToast({
                    show: true,
                    type: "success",
                    message: "Notes have been generated successfully",
                });
            }

            // If backend says generation is in progress, but we're not polling, start polling
            // This handles page refresh case.
            if (
                morningMeetingDetails.is_generating_notes &&
                !notesGenerationPollInterval &&
                !hasMeetingNotes
            ) {
                const pollInterval = setInterval(async () => {
                    await getMorningMeetingDetails(meetingId);
                }, NOTES_GENERATION_POLL_INTERVAL);

                setNotesGenerationPollInterval(pollInterval);

                // Stop polling after 5 minutes max
                setTimeout(() => {
                    if (pollInterval) {
                        clearInterval(pollInterval);
                        setNotesGenerationPollInterval(null);
                    }
                }, NOTES_GENERATION_POLL_TIMEOUT);
            }
        }
    }, [morningMeetingDetails, notesGenerationPollInterval, meetingId]);

    // Cleanup polling on unmount
    useEffect(() => {
        return () => {
            if (notesGenerationPollInterval) {
                clearInterval(notesGenerationPollInterval);
            }
        };
    }, [notesGenerationPollInterval]);

    return (
        <MorningMeetingDetailsContainer>
            {modalOpen && morningMeetingDetails?.date && (
                <MorningMeetingModal
                    title="EDIT MEETING"
                    onClose={() => setModalOpen(false)}
                    onSubmit={updateMorningMeeting}
                    meetingTitle={morningMeetingDetails?.title}
                    meetingDate={new Date(morningMeetingDetails?.date)}
                ></MorningMeetingModal>
            )}

            {fetchZoomFileModalType && (
                <FetchZoomFileModal
                    fileType={fetchZoomFileModalType}
                    onClose={() => setFetchZoomFileModalType(null)}
                    onConfirm={handleFetchZoomFileConfirm}
                />
            )}

            {recordingUrlModalOpen && (
                <FetchRecordingUrlModal
                    onClose={() => setRecordingUrlModalOpen(false)}
                    onConfirm={handleFetchRecordingUrlConfirm}
                />
            )}

            {downloadFilesModalOpen && (
                <DownloadMeetingFilesModal
                    onClose={() => setDownloadFilesModalOpen(false)}
                    files={availableMeetingFiles}
                    meetingId={meetingId}
                />
            )}

            <h1 className="page-title">
                {morningMeetingDetails?.title}
                {morningMeetingDetails?.cancelled && " (Cancelled)"}
            </h1>
            <div className="details">
                <Tooltip title={<h2>MEETING DATE</h2>}>
                    <div className={"detail"}>
                        <FontAwesomeIcon icon={faCalendar} fixedWidth />
                        <p className={"capitalize"}>
                            {toDateAndTimeSeconds(morningMeetingDetails?.date)}
                        </p>
                    </div>
                </Tooltip>
            </div>
            <hr />
            {shouldShowFetchTranscriptButton() && (
                <Alert
                    severity="info"
                    sx={{
                        marginTop: 2,
                        marginBottom: 2,
                        cursor: "default",
                    }}
                >
                    It's been a while and Zoom meeting transcript is still
                    missing.{" "}
                    <Link
                        component="button"
                        onClick={() => setFetchZoomFileModalType("TRANSCRIPT")}
                        sx={{ fontWeight: "bold", fontSize: "14px" }}
                    >
                        Click here
                    </Link>{" "}
                    to initiate its download.
                </Alert>
            )}
            {morningMeetingDetails?.is_generating_notes && (
                <Alert
                    severity="info"
                    sx={{
                        marginTop: 2,
                        marginBottom: 2,
                        cursor: "default",
                    }}
                >
                    Notes are being generated. This may take a few minutes. The
                    page will update automatically when ready.
                </Alert>
            )}
            <div className="actions-wrapper">
                {!isReordering && !morningMeetingDetails?.cancelled && (
                    <Button
                        text="ADD AGENDA ITEM"
                        icon={faPlus}
                        click={openNewAgendaItemPage}
                        size="medium"
                    />
                )}
                {(morningMeetingDetails?.agenda_items?.length || 0) > 0 &&
                    !isReordering && (
                        <Button
                            text="REORDER"
                            icon={faReorder}
                            click={() => {
                                setIsReordering(true);
                            }}
                            size="medium"
                        />
                    )}
                {isReordering && (
                    <Button
                        text="SAVE ORDER"
                        icon={faSave}
                        click={() => {
                            setIsReordering(false);
                            saveNewOrder();
                        }}
                        size="medium"
                    />
                )}
                {!isReordering && !morningMeetingDetails?.cancelled && (
                    <>
                        <div>
                            <Button
                                text="EDIT MEETING TIME"
                                icon={faPencil}
                                click={() => setModalOpen(true)}
                                size="medium"
                            />
                        </div>
                        {shouldShowWatchRecordingButton() && (
                            <div>
                                <Button
                                    text="WATCH RECORDING"
                                    icon={faVideo}
                                    click={handleWatchRecordingClick}
                                    size="medium"
                                />
                            </div>
                        )}
                        {shouldShowReviewGeneratedNotesButton() && (
                            <div>
                                <Button
                                    text="REVIEW GENERATED NOTES"
                                    icon={faCheck}
                                    click={() =>
                                        openGeneratedNotesPage("unconfirmed")
                                    }
                                    size="medium"
                                />
                            </div>
                        )}
                        {morningMeetingDetails?.contains_confirmed_notes && (
                            <div>
                                <Button
                                    text="VIEW GENERATED NOTES"
                                    icon={faEye}
                                    click={() =>
                                        openGeneratedNotesPage("confirmed")
                                    }
                                    size="medium"
                                />
                            </div>
                        )}
                        {shouldShowGenerateNotesButton() && (
                            <div>
                                <Button
                                    text={
                                        morningMeetingDetails?.is_generating_notes
                                            ? "GENERATING NOTES..."
                                            : "GENERATE NOTES"
                                    }
                                    icon={faFileText}
                                    click={generateNotes}
                                    disabled={
                                        morningMeetingDetails?.is_generating_notes
                                    }
                                    size="medium"
                                />
                            </div>
                        )}
                        {availableMeetingFiles.length > 0 && (
                            <div>
                                <Button
                                    text="DOWNLOAD FILES"
                                    icon={faDownload}
                                    click={() =>
                                        setDownloadFilesModalOpen(true)
                                    }
                                    size="medium"
                                />
                            </div>
                        )}
                        <Tooltip title={cancelMeetingReason} placement="top">
                            <div>
                                <Button
                                    text="APPROVE MEETING CANCELATION"
                                    icon={faMultiply}
                                    click={cancelMeeting}
                                    disabled={!canCancelMeeting}
                                    size="medium"
                                />
                            </div>
                        </Tooltip>
                    </>
                )}
            </div>
            {morningMeetingDetails && (
                <HomeList
                    data={morningMeetingDetails.agenda_items}
                    row={(row: AgendaItem, index: number) => (
                        <AgendaItemRow data={row} index={index} />
                    )}
                    title={"AGENDA ITEMS"}
                    countColor={""}
                    loading={loading}
                    maxRows={100}
                    count={morningMeetingDetails.agenda_items.length}
                    sortable={isReordering}
                    onReorder={reorderItems}
                />
            )}
        </MorningMeetingDetailsContainer>
    );
};
export default MorningMeetingDetails;
